import { Color } from '@fe/core';
import { FillProps, LineStyle, Point, Rect, Size, StrokeProps, TextProps, TextRectProps } from '../util';

export interface Drawer {
    drawText(text: string, p: Point, textProps: TextProps, displayProps: FillProps, rotation?: number): void;
    drawTextRect(text: string, p: Point, textProps: TextProps, displayProps: TextRectProps, rotation?: number): void;
    drawLine(p1: Point, p2: Point, displayProps: StrokeProps): void;
    drawRect(rect: Rect, fillProps: FillProps, strokeProps?: StrokeProps): void;
    drawPolygon(points: Point[], fillProps: FillProps, strokeProps?: StrokeProps): void;
    drawTriangle(p: Point, size: Size, fillProps: FillProps, strokeProps?: StrokeProps | null, rotation?: number): void;
    drawArea(p1: Point, p2: Point, height: number, fillProps: FillProps, strokeProps: StrokeProps): void;
    drawHollowCircle(p: Point, radius: number, strokeProps: StrokeProps): void;
    drawCircle(p: Point, radius: number, fillProps: FillProps): void;
    clear(): void;
    drawImage(image: HTMLImageElement, p: Point, size: Size): void;
    drawSprite(
        sprite: CanvasImageSource,
        sx: number,
        sy: number,
        sw: number,
        sh: number,
        x: number,
        y: number,
        w: number,
        h: number,
        rotation: number
    ): void;
    drawGradientRect(rect: Rect, colorData: [number, Color][]): void;
    beginClip(rect: Rect): void;
    endClip(): void;
    setFont(font: string): void;
    beginTransform(options: { translate?: [number, number] }): void;
    endTransform(): void;
    setScale(sx: number, sy: number): void;
    setIdentityTransform(): void;

    get ctx(): CanvasRenderingContext2D;
    get width(): number;
    get height(): number;
}

export function createDrawer(canvas: HTMLCanvasElement): Drawer {
    return new CanvasDrawer(canvas);
}

export class CanvasDrawer implements Drawer {
    private _canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D | null = null;

    constructor(canvas: HTMLCanvasElement) {
        this._canvas = canvas;
    }

    public get ctx(): CanvasRenderingContext2D {
        return (this.context ??= this.createContext());
    }

    public get canvas(): HTMLCanvasElement {
        return this._canvas;
    }

    public get width(): number {
        return this._canvas.clientWidth;
    }

    public get height(): number {
        return this._canvas.clientHeight;
    }

    public clear() {
        this.ctx.clearRect(0, 0, this.width, this.height);
    }

    setScale(sx: number, sy: number) {
        this.ctx.scale(sx, sy);
    }

    setFont(font: string) {
        this.ctx.font = font;
    }

    setIdentityTransform() {
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    beginClip(rect: Rect) {
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.rect(rect.left, rect.top, rect.width, rect.height);
        this.ctx.clip();
    }

    endClip() {
        this.ctx.restore();
    }

    beginTransform(options: { translate?: [number, number] }) {
        this.ctx.save();

        if (options.translate) {
            this.ctx.translate(...options.translate);
        }
    }

    endTransform() {
        this.ctx.restore();
    }

    public drawLine(p1: Point, p2: Point, displayProps: StrokeProps): void {
        const [x1, y1] = p1;
        const [x2, y2] = p2;

        this.ctx.beginPath();
        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.stroke(displayProps);
        this.ctx.setLineDash([]);
    }

    public drawText(text: string, p: Point, textProps: TextProps, fillProps: FillProps, rotation: number = 0) {
        const [x, y] = p;
        const { alignment: aligment, baseline, font } = textProps;
        const { color } = fillProps;

        const previousFont = this.ctx.font;
        if (font) {
            this.ctx.font = font;
        }
        this.ctx.fillStyle = color as string;
        this.ctx.textBaseline = <CanvasTextBaseline>baseline;
        this.ctx.textAlign = <CanvasTextAlign>aligment;
        if (rotation) {
            this.ctx.save();
            this.ctx.translate(x, y);
            this.ctx.rotate((rotation * Math.PI) / 180);
            this.ctx.fillText(text, 0, 0);
        } else {
            this.ctx.fillText(text, x, y);
        }

        if (rotation) {
            this.ctx.restore();
        }
        // restore the font
        this.ctx.font = previousFont;
    }

    drawTextRect(text: string, p: Point, textProps: TextProps, displayProps: TextRectProps, rotation?: number) {
        const [x, y] = p;
        const { alignment: aligment, baseline, font } = textProps;
        const previousFont = this.ctx.font;
        if (font) {
            this.ctx.font = font;
        }
        this.ctx.textBaseline = <CanvasTextBaseline>baseline;
        this.ctx.textAlign = <CanvasTextAlign>aligment;

        const measure = this.ctx.measureText(text);
        if (rotation) {
            this.ctx.save();
            this.ctx.translate(x, y);
            this.ctx.rotate((rotation * Math.PI) / 180);

            this.ctx.fillStyle = displayProps.fillColor as string;
            this.ctx.fillRect(
                -measure.actualBoundingBoxLeft - displayProps.padding,
                -measure.actualBoundingBoxAscent - displayProps.padding,
                measure.width + displayProps.padding * 2,
                measure.actualBoundingBoxAscent + measure.actualBoundingBoxDescent + displayProps.padding * 2
            );

            this.ctx.fillStyle = displayProps.textColor as string;
            this.ctx.fillText(text, 0, 0);
        } else {
            this.ctx.fillStyle = displayProps.fillColor as string;
            this.ctx.fillRect(
                x - measure.actualBoundingBoxLeft - displayProps.padding,
                y - measure.actualBoundingBoxAscent - displayProps.padding,
                measure.width + displayProps.padding * 2,
                measure.actualBoundingBoxAscent + measure.actualBoundingBoxDescent + displayProps.padding * 2
            );

            this.ctx.fillStyle = displayProps.textColor as string;
            this.ctx.fillText(text, x, y);
        }

        if (rotation) {
            this.ctx.restore();
        }

        // restore the font
        this.ctx.font = previousFont;
    }

    measureText(text: string, textProps: TextProps): TextMetrics {
        this.ctx.font = <string>textProps.font;
        return this.ctx.measureText(text);
    }

    public drawRect(rect: Rect, fillProps: FillProps, strokeProps?: StrokeProps): void {
        const { color } = fillProps;
        this.ctx.fillStyle = color as string;
        this.ctx.beginPath();
        this.ctx.fillRect(rect.left, rect.top, rect.width, rect.height);
        this.strokeRect(rect, strokeProps);
    }

    drawPolygon(points: Point[], fillProps: FillProps, strokeProps?: StrokeProps) {
        this.ctx.beginPath();
        this.ctx.moveTo(points[0][0], points[0][1]);
        for (const point of points) {
            this.ctx.lineTo(point[0], point[1]);
        }

        this.ctx.closePath();
        this.fill(fillProps);
        this.stroke(strokeProps);
    }

    public drawGradientRect(rect: Rect, colorData: [number, Color][]): void {
        const gradient = this.ctx.createLinearGradient(rect.left, rect.top, rect.right, rect.bottom);
        for (const cd of colorData) {
            gradient.addColorStop(cd[0], cd[1] as string);
        }
        this.ctx.beginPath();
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(rect.left, rect.top, rect.width, rect.height);
    }

    drawTriangle(p: Point, size: Size, fillProps: FillProps, strokeProps?: StrokeProps, rotation?: number): void {
        const { color } = fillProps;
        const [x, y] = p;
        const { width, height } = size;

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(((rotation ?? 0) * Math.PI) / 180);
        this.ctx.translate(-x, -y);
        this.ctx.fillStyle = color as string;
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
        this.ctx.lineTo(x + width / 2, height + y);
        this.ctx.lineTo(x - width / 2, height + y);
        this.ctx.closePath();
        this.ctx.fill();
        this.stroke(strokeProps);
        this.ctx.restore();
    }

    public drawArea(p1: Point, p2: Point, base: number, filProps: FillProps, strokeProps?: StrokeProps): void {
        const { color } = filProps;
        const points: [number, number][] = [p1, p2, [p2[0], base], [p1[0], base]];

        // Draw the polygon
        this.ctx.beginPath();
        this.ctx.moveTo(points[0][0], points[0][1]);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i][0], points[i][1]);
        }

        this.ctx.closePath();
        this.ctx.fillStyle = color as string;
        this.stroke(strokeProps);
        this.ctx.fill();
    }

    public drawImage(image: HTMLImageElement, p: Point, size: Size): void {
        const [x, y] = p;
        const { width, height } = size;
        this.ctx.drawImage(image, x, y, width, height);
    }

    drawSprite(
        sprite: HTMLImageElement,
        sx: number,
        sy: number,
        sw: number,
        sh: number,
        x: number,
        y: number,
        w: number,
        h: number,
        rotation: number
    ) {
        this.ctx.save();
        this.ctx.globalAlpha = 0.5;
        this.ctx.translate(x, y);
        this.ctx.rotate((rotation / 180) * Math.PI);
        this.ctx.drawImage(sprite, sx, sy, sw, sh, -0.5 * w, -0.5 * h, w, h);
        this.ctx.restore();
    }

    public drawHollowCircle(p: Point, radius: number, strokeProps: StrokeProps) {
        const [x, y] = p;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
        this.ctx.closePath();
        this.stroke(strokeProps);
    }

    public drawCircle(p: Point, radius: number, fillProps: FillProps) {
        const [x, y] = p;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
        this.ctx.closePath();
        this.fill(fillProps);
    }

    private createContext(): CanvasRenderingContext2D {
        const ctx = this.canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Cannot get canvas context');
        }
        return ctx;
    }

    private stroke(strokeProps?: StrokeProps): void {
        if (!strokeProps) {
            return;
        }
        const { color, width = 1, style = LineStyle.solid } = strokeProps;
        this.ctx.strokeStyle = color as string;
        this.ctx.lineWidth = width;
        this.ctx.setLineDash(style === LineStyle.dashed ? [4, 2] : []);
        this.ctx.stroke();
    }

    private fill(fillProps: FillProps): void {
        this.ctx.fillStyle = fillProps.color as string;
        this.ctx.fill();
    }

    private strokeRect(rect: Rect, strokeProps?: StrokeProps): void {
        if (!strokeProps) {
            return;
        }
        const { color, width = 1, style = LineStyle.solid } = strokeProps;
        this.ctx.strokeStyle = color as string;
        this.ctx.lineWidth = width;
        this.ctx.setLineDash(style === LineStyle.dashed ? [4, 2] : []);
        this.ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);
    }
}
